import { useCallback, useState } from 'react'
import './ChangeToSentPopUp.scss'
import { ConfirmationPopup } from '../../../../components/confirmation-popup/confirmationpopup'
import Portal from '../../../../components/portal/Portal'
import { ErrorAlert, PageLoadingSpinner } from 'ui_kit_qmd'
import successIcon from '../../../../images/common/success.png'
import errorIcon from '../../../../images/common/error-icon.svg'
import { useDispatch } from 'react-redux'
import { getPatientPrescriptions } from '../../../../store/charts/ChartsThunk'
import { getErrorObject, getValue } from '@quickmd-llc/frontend-utils'
import { useQueryClient } from '@tanstack/react-query'
import { getGetPatientsPatientIdPrescriptionsQueryKey } from '../../../../api/generated/prescription/prescription'
import { usePutOrdersSent } from '../../../../api/generated/order/order'

const TITLE = 'Confirm status update'
const MESSAGE = 'This will change the prescription status to "Sent to pharmacy".'
const QUESTION = 'Are you sure you wish to proceed?'

interface Props {
  onClosePopUp: () => void
  orderId: string
  patientId: string
}

export const ChangeToSentPopUp = ({ onClosePopUp, orderId, patientId }: Props) => {
  const dispatch = useDispatch()
  const queryClient = useQueryClient()
  const [error, setError] = useState('')
  const { mutate, isPending } = usePutOrdersSent()

  const showError = useCallback((msg: string) => {
    setError(msg)
    setTimeout(() => setError(''), 3500)
  }, [])

  const handleChangeToSent = useCallback(() => {
    mutate(
      { data: { PrescriptionId: orderId, PatientId: patientId } },
      {
        onSuccess: () => {
          // @ts-expect-error TS2345 TEMP ERROR SUPPRESSION: Redux thunk
          dispatch(getPatientPrescriptions(patientId))
          void queryClient.invalidateQueries({ queryKey: getGetPatientsPatientIdPrescriptionsQueryKey(patientId) })
          onClosePopUp()
        },
        onError: (e: unknown) => {
          const errorCode = getValue(e, 'ErrorCode')
          const message =
            errorCode === 'Permission' ? 'You do not have permission to perform this action'
            : errorCode === 'PatientNotFound' ? 'Patient not found'
            : errorCode === 'PrescriptionNotFound' ? 'Prescription not found for this patient'
            : errorCode === 'MarkAsSentNotAllowed' ?
              'This prescription cannot be marked as sent — it is not currently cancelled'
            : (getErrorObject(e).message ?? 'Internal server error')
          showError(message)
        },
      },
    )
  }, [orderId, patientId, onClosePopUp, showError, dispatch, queryClient, mutate])

  return (
    <Portal>
      {isPending ?
        <PageLoadingSpinner />
      : null}
      {error ?
        <ErrorAlert styleContainer={{ width: 550, height: 56 }} errorText={error} errorImg={errorIcon} imgAlt='Error' />
      : null}
      <ConfirmationPopup
        title={TITLE}
        titleIcon={successIcon}
        className='change-to-sent-pop-up-container'
        id='change-to-sent-confirmation-pop-up'
        primaryBtnText='Confirm status update'
        primaryBtnAction={handleChangeToSent}
        secondaryBtnText='Go back'
        secondaryBtnAction={onClosePopUp}
        closeAction={undefined}
        isCaution={false}
        msg={
          <div className='change-to-sent-message-container'>
            <div className='change-to-sent-message'>{MESSAGE}</div>
            <div className='change-to-sent-question'>{QUESTION}</div>
          </div>
        }
        popUpBoxStyle={undefined}
        extraView={undefined}
        buttonWidth={undefined}
      />
    </Portal>
  )
}
